import React from 'react';

import { AUTH0_CONFIG } from './plugins/auth/auth.config';
import { AuthenticatedUserProvider, useRouterAuth } from './plugins/auth/Authentication';
import { ErrorBoundary } from 'react-error-boundary';
import { Logger } from './plugins/logger/Logger';
import { ErrorFallback } from './pages/error/ErrorFallback';
import { RouterProvider } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { createAssetLibraryApiClient } from '@ael/core-api';
import { ModalPortal } from './components/atoms/portals/ModalPortal';

import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { createDefaultQueryClient } from './globalObjects';
import { Agentation } from 'agentation';
import { global_log_cache } from './rest-api/fetsLogPlugin';
import { router } from './router';
import { createApiClient } from './rest-api/fetsClient';
import { createPortalApiClient } from './rest-api/portalApiClient';
import { API_CONFIG } from './api/api.config';
import { FullScreenSize } from './components/atoms/layouts/FullScreenSize';
import { Centered } from './components/atoms/layouts/Centered';
import { Loader } from './components/molecules/loader/Loader';

// Must live inside Auth0Provider + QueryClientProvider: the router context
// (api client bound to Auth0 tokens, query client) is injected here.
const InnerApp = () => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const auth = useRouterAuth();
  const [apiClient] = React.useState(() =>
    createPortalApiClient(createApiClient(API_CONFIG.BACKEND_URL, getAccessTokenSilently))
  );

  const [assetLibraryApiClient] = React.useState(() =>
    createAssetLibraryApiClient(API_CONFIG.BACKEND_URL, getAccessTokenSilently)
  );

  if (auth.isLoading) {
    return (
      <FullScreenSize>
        <Centered tryGoldenCenter>
          <Loader label="Authenticating..." />
        </Centered>
      </FullScreenSize>
    );
  }

  return <RouterProvider router={router} context={{ queryClient, apiClient, assetLibraryApiClient, auth }} />;
};

export const EntryPoint = () => {
  const [queryClient] = React.useState(createDefaultQueryClient);

  return (
    <>
      <AuthenticatedUserProvider
        domain={AUTH0_CONFIG.AUTH0_DOMAIN}
        audience={AUTH0_CONFIG.AUTH0_AUDIENCE}
        clientId={AUTH0_CONFIG.AUTH0_CLIENT_ID}
        // Kept: switching the cache would sign every open session out.
        cacheLocation="localstorage"
        redirectUri={window.location.origin}
        screen_hint={window.location.pathname === '/signup' ? 'signup' : 'login'}
      >
        <ErrorBoundary FallbackComponent={ErrorFallback} onError={(error) => Logger.trackException(error)}>
          <QueryClientProvider client={queryClient}>
            <InnerApp />
            {import.meta.env.DEV && (
              <TanStackDevtools
                config={{ position: 'bottom-left' }}
                plugins={[
                  {
                    name: 'TanStack Query',
                    render: <ReactQueryDevtoolsPanel />,
                    defaultOpen: true,
                  },
                  {
                    name: 'TanStack Router',
                    // explicit router: the devtools mount outside RouterProvider
                    render: <TanStackRouterDevtoolsPanel router={router} />,
                  },
                ]}
              />
            )}
            {import.meta.env.DEV && (
              <Agentation
                onAnnotationAdd={(annotation) => {
                  const networkTraffic = global_log_cache ? global_log_cache.dump() : '(no log cache)';
                  annotation.comment = `**Annotation:** ${annotation.comment}\n\n**Network traffic context at time of annotation:**\n\n${networkTraffic}`;
                }}
              />
            )}
          </QueryClientProvider>
        </ErrorBoundary>
      </AuthenticatedUserProvider>
      {/* Above `z-modal` (99999), so a toast raised from inside an open modal isn't hidden behind it. */}
      <Toaster position="top-right" containerStyle={{ zIndex: 100000 }} />
      <ModalPortal />
    </>
  );
};
